/**
 * CardOverlayLink — the stretched link that makes a whole card a target,
 * on the layer the card seam says it belongs to (LET-85).
 *
 * This is the app-side half of `@letsgethome/ui/card-layers`: the seam owns
 * the class list (`absolute inset-0`, the overlay layer, the focus ring) and
 * this file only binds it to the locale-aware `Link`, which `packages/ui`
 * cannot import. A card composes THIS beside its `SlideCarousel` /
 * `SaveButton` — never around them — so no `<button>` ever sits inside an
 * `<a>`, and the controls win the hit test because `Z_LAYERS.cardControls.z >
 * Z_LAYERS.cardOverlayLink.z` (tested), not because someone remembered `z-20`.
 *
 * The anchor is EMPTY by design: the card's visible text is a sibling, so
 * the accessible name is the required `aria-label`. `prefetch="intent"` is
 * the default every card link already carried.
 *
 * ⛔ THERE IS NO `onActivate` HERE, AND THAT IS DELIBERATE (obs. 4). The prop
 * existed for exactly one caller — `ListingCard`, which forwarded the hub's
 * card-selection handler into it. `EnhancedLink` `preventDefault()`s the plain
 * primary click whenever an `onActivate` is present, so passing one turned a
 * card into a control that had to be clicked TWICE: once to select, once to
 * open. Measured at both viewports before the fix. With the prop deleted rather
 * than merely unused, "a card whose click is hijacked" is not representable;
 * a surface that needs to know where the reader IS asks `ListingCard` for
 * `onHighlightChange`, which cannot reach the click. `EnhancedLink` keeps its own
 * `onActivate` for the surfaces that legitimately open a DIALOG instead of
 * navigating (the account menus, the inquiry form, the preferences rows) —
 * this seam is the one place a link IS the whole card, and there the click has
 * exactly one meaning.
 *
 * ⛔ AND THERE IS NO `onClick` EITHER, WHICH IS THE SAME PROP ONE LAYER DOWN
 * (§2.14). `EnhancedLink` accepts an `onClick` for Radix `asChild` composition
 * and runs it BEFORE the seam; `isBrowserManagedClick` then honours
 * `defaultPrevented` (`routing.tsx`), so a caller handing this seam a
 * `preventDefault()`ing handler consumes the click and rebuilds the two-click
 * card `onActivate` was deleted for — through a prop whose doc said
 * "composition". The `preventDefaultOutsideSeam` arm of
 * `no-navigation-in-activation-handler` cannot report it: it sees a
 * same-file synchronous `preventDefault()` on an element with an `href`, and a
 * handler forwarded through a prop is neither. Deleting the prop needs no lint
 * at all — "a link-that-is-a-card with a click handler" is now a TYPE error for
 * every host. Nothing in the app ever passed it; the one caller was a test's
 * spy, which now rides on the mocked anchor instead.
 */
import { EnhancedLink } from '@/shared/i18n/routing';
import type { Route } from '@letsgethome/routes';
import { cardOverlayLinkClassName } from '@letsgethome/ui/card-layers';

type CardOverlayLinkProps = {
  href: Route;
  /** The accessible name of the card — the anchor has no text of its own. */
  'aria-label': string;
  /** The card's radius only, so the focus ring follows the card's shape. */
  className?: string;
};

export function CardOverlayLink({ className, ...link }: CardOverlayLinkProps) {
  return (
    <EnhancedLink prefetch="intent" {...link} className={cardOverlayLinkClassName(className)} />
  );
}
